import { useEffect, useRef } from 'react';

import { Camera, RemoteCursor } from '@/features/canvas/types';
import { worldToScreen } from './geometry';

// Per-frame easing of a remote cursor toward its last broadcast position. Interpolating in WORLD space
// (then converting with the CURRENT camera every frame) keeps peers' cursors buttery-smooth between the
// ~25Hz updates, yet perfectly pinned to the world as YOU pan/zoom.
const CURSOR_EASE = 0.3;
const HOTSPOT = 2; // px — the arrow tip sits ~2px in from the top-left

/** Live remote pointers, drawn in screen space (converted from the world coords each peer sends). */
export default function Cursors({ cam, cursors }: { cam: Camera; cursors: RemoteCursor[] }) {
  const camRef = useRef(cam);
  camRef.current = cam;
  const targets = useRef<Map<string, RemoteCursor>>(new Map());
  const anim = useRef<Map<string, { x: number; y: number }>>(new Map());
  const nodes = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    targets.current = new Map(cursors.map((c) => [c.clientId, c]));
  }, [cursors]);

  // One rAF loop for the overlay's lifetime: ease each cursor's world position toward its target and write
  // the screen transform imperatively (no React re-render per frame).
  useEffect(() => {
    let raf = requestAnimationFrame(function tick() {
      const c = camRef.current;
      targets.current.forEach((t, id) => {
        let a = anim.current.get(id);
        if (!a) {
          a = { x: t.x, y: t.y };
          anim.current.set(id, a);
        }
        a.x += (t.x - a.x) * CURSOR_EASE;
        a.y += (t.y - a.y) * CURSOR_EASE;
        const node = nodes.current.get(id);
        if (node) {
          const s = worldToScreen(c, a.x, a.y);
          node.style.transform = `translate(${s.x - HOTSPOT}px, ${s.y - HOTSPOT}px)`;
        }
      });
      anim.current.forEach((_, id) => {
        if (!targets.current.has(id)) anim.current.delete(id);
      });
      raf = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="pointer-events-none absolute inset-0 z-40 overflow-hidden">
      {cursors.map((c) => {
        const init = worldToScreen(camRef.current, c.x, c.y);
        return (
          <div
            key={c.clientId}
            ref={(el) => {
              if (el) {
                nodes.current.set(c.clientId, el);
                if (!anim.current.has(c.clientId)) anim.current.set(c.clientId, { x: c.x, y: c.y });
              } else {
                nodes.current.delete(c.clientId);
              }
            }}
            className="absolute left-0 top-0 will-change-transform"
            style={{ transform: `translate(${init.x - HOTSPOT}px, ${init.y - HOTSPOT}px)` }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill={c.color} style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.35))' }}>
              <path d="M5 3l6 16 2-6 6-2z" />
            </svg>
            {c.name ? (
              <span
                className="ml-3 -mt-1 inline-block whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-medium text-white"
                style={{ background: c.color }}
              >
                {c.name}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
